import {
  ALL_CHANNELS,
  Avatar,
  ChannelIcon,
  Empty,
  ErrorState,
  IconButton,
  Loading,
  Select,
  useFormat,
} from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useTeamName, useTeams } from '../pages/Teams';
import type { ConversationItem } from './types';

export interface ListFilters {
  status: string;
  assignee: string;
  channel: string;
  /** '' (all), 'mine' (my teams), 'none' or a team id. */
  team: string;
  /** Customer name, phone or email. */
  q: string;
  /** '' | 'overdue' | 'dueSoon' (first-reply SLA). */
  sla: string;
  /** '' (everything), 'chat' (messages), 'engagement' (comments and reviews), 'comment', 'review'. */
  kind?: string;
}

/** A star rating, read out as "4 out of 5 stars". */
export function Stars({ n }: { n: number }) {
  const { t } = useTranslation();
  return (
    <span className="stars" role="img" aria-label={t('inbox.stars', { count: n })}>
      {'★'.repeat(n)}
      <span className="stars-off">{'★'.repeat(Math.max(0, 5 - n))}</span>
    </span>
  );
}

function SlaBadge({ c }: { c: ConversationItem }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (c.firstResponseAt || !c.firstResponseDueAt || c.status === 'closed') return null;
  const late = !!c.slaBreachedAt || new Date(c.firstResponseDueAt) < new Date();
  return (
    <span className={`badge ${late ? 'badge-danger' : 'badge-warning'}`} data-testid="sla-badge">
      {late ? t('inbox.sla.badgeLate') : t('inbox.sla.badgeDue', { rel: fmt.relative(c.firstResponseDueAt) })}
    </span>
  );
}

export function ConversationList({
  filters,
  setFilters,
  active,
  onSelect,
  flash,
}: {
  filters: ListFilters;
  setFilters: (f: ListFilters) => void;
  active: string | null;
  onSelect: (id: string) => void;
  flash: string | null;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const teams = useTeams();
  const teamName = useTeamName();
  // Search as you type, without a request per keystroke.
  const [search, setSearch] = useState(filters.q);
  useEffect(() => setSearch(filters.q), [filters.q]);
  useEffect(() => {
    const h = setTimeout(() => {
      if (search.trim() !== filters.q) setFilters({ ...filters, q: search.trim() });
    }, 250);
    return () => clearTimeout(h);
  }, [search, filters, setFilters]);
  const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]);
  // Kind, assignee, channel and team fold away behind one button; it shows how many are set.
  const activeFilters = [filters.kind, filters.assignee !== 'any', filters.channel, filters.team].filter(
    Boolean,
  ).length;
  const [showFilters, setShowFilters] = useState(activeFilters > 0);
  const q = useQuery({
    queryKey: ['conversations', filters],
    queryFn: () => api<{ items: ConversationItem[] }>(`/v1/conversations?${params}`),
  });
  return (
    <>
      <div className="inbox-filters">
        <div className="inbox-search-row">
          <label className="inbox-search">
            <Search size={14} aria-hidden="true" className="faint" />
            <span className="sr-only">{t('inbox.search')}</span>
            <input
              type="search"
              className="input"
              dir="auto"
              value={search}
              placeholder={t('inbox.searchPlaceholder')}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="inbox-search"
            />
          </label>
          <IconButton
            icon={SlidersHorizontal}
            label={t('inbox.filters.more')}
            onClick={() => setShowFilters(!showFilters)}
            active={showFilters}
            badge={activeFilters}
            side="end"
            aria-expanded={showFilters}
            data-testid="filters-toggle"
          />
        </div>
        <div
          className="segmented"
          role="group"
          aria-label={t('inbox.statusLabel')}
          data-testid="status-filter"
        >
          {(['open', 'pending', 'closed', 'all'] as const).map((s) => (
            <button
              key={s}
              aria-pressed={filters.status === s}
              onClick={() => setFilters({ ...filters, status: s })}
            >
              {t(`inbox.filters.${s}`)}
            </button>
          ))}
        </div>
        {filters.sla && (
          <button
            type="button"
            className="chip filter-chip"
            onClick={() => setFilters({ ...filters, sla: '' })}
          >
            {t(`inbox.overview.${filters.sla}`)} ×
          </button>
        )}
        <div className="inbox-filter-selects" hidden={!showFilters}>
          <Select
            value={filters.kind ?? ''}
            onChange={(v) => setFilters({ ...filters, kind: v })}
            aria-label={t('inbox.kindFilter.label')}
            data-testid="kind-filter"
            options={(['', 'chat', 'engagement', 'comment', 'review'] as const).map((k) => ({
              value: k,
              label: t(`inbox.kindFilter.${k || 'all'}`),
            }))}
          />
          <Select
            value={filters.assignee}
            onChange={(v) => setFilters({ ...filters, assignee: v })}
            aria-label={t('inbox.assignee')}
            data-testid="assignee-filter"
            options={[
              { value: 'any', label: t('inbox.filters.everyone') },
              { value: 'me', label: t('inbox.filters.mine') },
              { value: 'unassigned', label: t('inbox.filters.unassigned') },
            ]}
          />
          <Select
            value={filters.channel}
            onChange={(v) => setFilters({ ...filters, channel: v })}
            aria-label={t('crm.people.col.channels')}
            data-testid="channel-filter"
            options={[
              { value: '', label: t('inbox.filters.allChannels') },
              ...ALL_CHANNELS.map((c) => ({
                value: c,
                label: t(`channels.${c}`),
                icon: <ChannelIcon channel={c} size={11} />,
              })),
            ]}
          />
          {!!teams.data?.items.length && (
            <Select
              value={filters.team}
              onChange={(v) => setFilters({ ...filters, team: v })}
              aria-label={t('inbox.team')}
              data-testid="team-filter"
              options={[
                { value: '', label: t('inbox.filters.allTeams') },
                { value: 'mine', label: t('inbox.filters.myTeams') },
                { value: 'none', label: t('inbox.filters.noTeam') },
                ...teams.data.items.map((x) => ({ value: x.id, label: teamName(x) })),
              ]}
            />
          )}
        </div>
      </div>
      {q.isLoading && <Loading />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data?.items.length === 0 && <Empty text={t('inbox.empty')} />}
      <ul className="conv-list" data-testid="conversation-list">
        {q.data?.items.map((c) => (
          <li key={c.id}>
            <button
              className={`conv-item${flash === c.id ? ' flash' : ''}`}
              aria-current={c.id === active}
              onClick={() => onSelect(c.id)}
            >
              <span className="ch channel-badge">
                <ChannelIcon channel={c.channel} size={13} />
              </span>
              <span className="who" dir="auto">
                {c.personDisplayName}
              </span>
              <span className="when">{c.lastMessageAt ? fmt.relative(c.lastMessageAt) : ''}</span>
              <span className="preview" dir="auto">
                {c.lastDirection === 'outbound' ? '↩ ' : ''}
                {c.lastMessagePreview}
              </span>
              <span className="conv-tags">
                <SlaBadge c={c} />
                {c.kind === 'comment' && <span className="badge badge-neutral">{t('inbox.comment')}</span>}
                {c.kind === 'review' && (
                  <span className="badge badge-neutral" data-testid="review-badge">
                    {c.rating ? <Stars n={c.rating} /> : t('inbox.review')}
                  </span>
                )}
                {c.fromAd && <span className="badge">{t('inbox.fromAd')}</span>}
                {c.assistantActive && (
                  <span className="badge ai-pill" title={t('assistant.handling')} data-testid="ai-badge">
                    {t('assistant.ai')}
                  </span>
                )}
                {c.teamId && (
                  <span className="badge badge-neutral team-tag" dir="auto">
                    {teamName({ name: c.teamName ?? '', nameAr: c.teamNameAr })}
                  </span>
                )}
                {c.assigneeName && (
                  <span title={c.assigneeName}>
                    <Avatar name={c.assigneeName} size={20} />
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
